import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Command, HelpCircle, Menu, Moon, Sun, X } from "lucide-react";
import { searchCommandPalette, type CommandPaletteResult } from "@/api/commandPalette";
import { useArtistScope } from "@/lib/artistContext";
import { useAuth } from "@/lib/authContext";
import { normalizePlanKey } from "@/lib/plans";
import OwnerGate from "@/components/OwnerGate";
import { useSessionUser } from "@/hooks/useSessionUser";
import { useTheme } from "@/providers/ThemeProvider";

type NavMenuLink = {
  type?: "link";
  label: string;
  to: string;
  description?: string;
  ownerOnly?: boolean;
  requiresPro?: boolean;
};

type NavMenuGroup = {
  type: "group";
  title: string;
  links: NavMenuLink[];
};

type NavMenuSection = {
  type: "section";
  title: string;
  entries: (NavMenuLink | NavMenuGroup)[];
  ownerOnly?: boolean;
};

type NavPillar = {
  id: string;
  label: string;
  to: string;
  items?: (NavMenuLink | NavMenuSection)[];
  menuWidth?: number;
  columns?: number;
};

type CommandPaletteRoute = {
  id: string;
  type: "route";
  label: string;
  to: string;
  description?: string;
  group: string;
  ownerOnly?: boolean;
  requiresPro?: boolean;
};

type CommandPaletteAction = {
  id: string;
  type: "action";
  label: string;
  description?: string;
  group: string;
  ownerOnly?: boolean;
  requiresPro?: boolean;
  actionId: "toggle_theme" | "sign_out" | "crm_create_contact";
};

type CommandPaletteResultEntry = {
  id: string;
  type: "result";
  label: string;
  description?: string | null;
  to?: string | null;
  group: string;
  meta?: Record<string, any> | null;
};

type CommandPaletteItem = CommandPaletteRoute | CommandPaletteAction | CommandPaletteResultEntry;

const CORE_AGENT_LINKS: NavMenuLink[] = [
  { label: "AI Workspace", to: "/agents", description: "All agents in one place" },
  { label: "Manager Agent", to: "/agents?agent=manager" },
  { label: "Booking Agent", to: "/agents?agent=booking" },
  { label: "Marketing Agent", to: "/agents?agent=marketing" },
  { label: "Publicist Agent", to: "/agents?agent=publicist" },
  { label: "Email Agent", to: "/agents?agent=email" },
  { label: "Routing Agent", to: "/routing" },
];

const TOOLS_LINKS: NavMenuLink[] = [
  { label: "Contacts", to: "/contacts" },
  { label: "Find Artists", to: "/artists" },
  { label: "Booking", to: "/booking" },
  { label: "Email", to: "/email" },
  { label: "Calendar", to: "/calendar" },
  { label: "Venue Map", to: "/venue-map" },
  { label: "Find Venues", to: "/venues/ingestion" },
  { label: "Accounting", to: "/accounting" },
  { label: "Music Analytics", to: "/marketing/music-analytics", ownerOnly: true },
];

const MARKETING_LINKS: NavMenuLink[] = [{ label: "Marketing Library", to: "/marketing/library", ownerOnly: true }];

const EXECUTION_LINKS: NavMenuLink[] = [{ label: "Agent History", to: "/memory" }];

const CONTROL_LINKS: NavMenuLink[] = [{ label: "Admin Settings", to: "/control/master" }];

const OBSERVABILITY_LINKS: NavMenuLink[] = [{ label: "Agent Activity", to: "/agents/observability", ownerOnly: true }];

const AGENTS_STUDIOS_MENU: NavMenuSection[] = [
  { type: "section", title: "Core Agents", entries: CORE_AGENT_LINKS },
];

const TOOLS_MENU: NavMenuSection[] = [{ type: "section", title: "Tools", entries: TOOLS_LINKS }];
const MARKETING_MENU: NavMenuSection[] = [{ type: "section", title: "Marketing", entries: MARKETING_LINKS }];
const CONTROL_MENU: NavMenuSection[] = [
  { type: "section", title: "Control", entries: CONTROL_LINKS },
  {
    type: "section",
    title: "Executive",
    ownerOnly: true,
    entries: [
      { label: "Executive Dashboard", to: "/executive", ownerOnly: true },
      ...OBSERVABILITY_LINKS,
      { label: "Workflow Builder", to: "/workflows/builder", ownerOnly: true },
      { label: "Workflow Triggers", to: "/automation/triggers", ownerOnly: true },
    ],
  },
  { type: "section", title: "Execution & Memory", entries: EXECUTION_LINKS },
];
// MVP primary navigation. Legacy/internal surfaces stay routable but are not
// part of the customer navigation (owners can still reach them via the palette).
const NAV_PILLARS: NavPillar[] = [
  { id: "home", label: "Home", to: "/dashboard" },
  { id: "discover", label: "Discover", to: "/discover" },
  { id: "booking", label: "Booking", to: "/booking" },
  { id: "shows", label: "Shows", to: "/shows" },
  { id: "business", label: "Business", to: "/business" },
];
const COMMAND_ROUTES: CommandPaletteRoute[] = [
  { id: "route-home", type: "route", label: "Home", to: "/dashboard", group: "Go to" },
  { id: "route-discover", type: "route", label: "Discover", to: "/discover", group: "Go to" },
  { id: "route-booking", type: "route", label: "Booking", to: "/booking", group: "Go to" },
  { id: "route-shows", type: "route", label: "Shows", to: "/shows", group: "Go to" },
  { id: "route-business", type: "route", label: "Business", to: "/business", group: "Go to", ownerOnly: true },
  { id: "route-settings", type: "route", label: "Settings", to: "/settings", group: "Settings" },
  { id: "route-artist-settings", type: "route", label: "Artist profile", to: "/settings/artist", group: "Settings" },
  { id: "route-billing", type: "route", label: "Billing", to: "/settings/billing", group: "Settings" },

  // Legacy/internal surfaces: owner-only in the palette, still routable.
  { id: "route-crm", type: "route", label: "Contacts", to: "/contacts", group: "Admin & legacy", ownerOnly: true },
  { id: "route-email", type: "route", label: "Email", to: "/email", group: "Admin & legacy", ownerOnly: true },
  { id: "route-venue-map", type: "route", label: "Venue Map", to: "/venue-map", group: "Admin & legacy", ownerOnly: true },
  { id: "route-venue-queue", type: "route", label: "Venue Queue", to: "/venue-queue", group: "Admin & legacy", ownerOnly: true },
  { id: "route-venue-ingestion", type: "route", label: "Find Venues (ingestion)", to: "/venues/ingestion", group: "Admin & legacy", ownerOnly: true },
  { id: "route-artists", type: "route", label: "Find Artists (legacy)", to: "/artists", group: "Admin & legacy", ownerOnly: true },
  { id: "route-ai-workspace", type: "route", label: "AI Workspace", to: "/agents", group: "Admin & legacy", ownerOnly: true },
  { id: "route-routing-agent", type: "route", label: "Routing", to: "/routing", group: "Admin & legacy", ownerOnly: true },
  { id: "route-hitl", type: "route", label: "Approvals", to: "/hitl/inbox", group: "Admin & legacy", ownerOnly: true },
  { id: "route-agent-activity", type: "route", label: "Agent Activity", to: "/agents/observability", group: "Admin & legacy", ownerOnly: true },
  { id: "route-marketing-library", type: "route", label: "Marketing Library", to: "/marketing/library", group: "Admin & legacy", ownerOnly: true },
  { id: "route-music-analytics", type: "route", label: "Music Analytics", to: "/marketing/music-analytics", group: "Admin & legacy", ownerOnly: true },
  { id: "route-pressroom", type: "route", label: "Pressroom", to: "/pressroom", group: "Admin & legacy", ownerOnly: true },
  { id: "route-executive", type: "route", label: "Executive Dashboard", to: "/executive", group: "Admin & legacy", ownerOnly: true },
  { id: "route-workflow-builder", type: "route", label: "Workflow Builder", to: "/workflows/builder", group: "Admin & legacy", ownerOnly: true },
  { id: "route-workflow-triggers", type: "route", label: "Workflow Triggers", to: "/automation/triggers", group: "Admin & legacy", ownerOnly: true },
  { id: "route-memory", type: "route", label: "Agent History", to: "/memory", group: "Admin & legacy", ownerOnly: true },
  { id: "route-master-control", type: "route", label: "Admin Settings", to: "/control/master", group: "Admin & legacy", ownerOnly: true },
  { id: "route-mixboard", type: "route", label: "Mixboard Studio", to: "/mixboard", group: "Admin & legacy", ownerOnly: true },
];

const ACCOUNT_MENU_LINKS: { label: string; to: string; teamOnly?: boolean }[] = [
  { label: "Settings", to: "/settings" },
  { label: "Artist profile", to: "/settings/artist" },
  { label: "Billing", to: "/settings/billing" },
  { label: "Team", to: "/settings/org", teamOnly: true },
];

const COMMAND_ACTIONS: CommandPaletteAction[] = [
  {
    id: "action-create-contact",
    type: "action",
    label: "Create CRM Contact",
    description: "Open the CRM contact drawer",
    group: "Actions",
    ownerOnly: true,
    actionId: "crm_create_contact",
  },
  {
    id: "action-toggle-theme",
    type: "action",
    label: "Toggle Theme",
    description: "Switch light/dark mode",
    group: "Actions",
    actionId: "toggle_theme",
  },
  {
    id: "action-sign-out",
    type: "action",
    label: "Sign Out",
    description: "End this session",
    group: "Actions",
    actionId: "sign_out",
  },
];

export default function TopNav() {
  const { artists, selectedArtistId, selectArtist, isLoading, error, isMultiArtist } = useArtistScope();
  const { signout } = useAuth();
  const { user, tier, isOnTrial, trialDaysLeft, trialExpired, hasPermission } = useSessionUser();
  const canManageTeam = Boolean(hasPermission?.("manage_agents"));
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState("");
  const [paletteLoading, setPaletteLoading] = useState(false);
  const [paletteRemoteResults, setPaletteRemoteResults] = useState<CommandPaletteResult[]>([]);
  const [paletteRemoteError, setPaletteRemoteError] = useState<string | null>(null);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [avatarFallback, setAvatarFallback] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);
  const helpMenuRef = useRef<HTMLDivElement | null>(null);
  const navRowRef = useRef<HTMLDivElement | null>(null);
  const navItemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const closeMenuTimeoutRef = useRef<number | null>(null);
  const paletteSearchTimeoutRef = useRef<number | null>(null);
  const paletteSearchRequestRef = useRef(0);
  const [menuPosition, setMenuPosition] = useState<{ left: number; top: number } | null>(null);
  const [menuHover, setMenuHover] = useState(false);
  const normalizedUserType = user?.user_type?.toLowerCase() ?? "";
  const isOwner = Boolean(user?.is_owner || normalizedUserType === "owner");
  const sessionPlan = normalizePlanKey(tier ?? user?.role ?? null);
  const canViewAgentsNav = ["PRO", "AGENCY", "OWNER"].includes(sessionPlan);
  const canViewMarketingNav = isOwner;
  const avatarSrc = !avatarFallback && user?.avatar_url ? user.avatar_url : "/aafavicon.png";
  const logoRoute = user ? "/dashboard" : "/";
  const artistOptions = useMemo(() => artists ?? [], [artists]);
  const paletteQueryValue = paletteQuery.trim();
  const accessibleRoutes = useMemo(
    () =>
      COMMAND_ROUTES.filter(
        (item) => (item.ownerOnly ? isOwner : true) && (!item.requiresPro || canViewMarketingNav),
      ),
    [canViewMarketingNav, isOwner],
  );
  const accessibleActions = useMemo(
    () =>
      COMMAND_ACTIONS.filter(
        (item) => (item.ownerOnly ? isOwner : true) && (!item.requiresPro || canViewMarketingNav),
      ),
    [canViewMarketingNav, isOwner],
  );
  const matchesPaletteQuery = (item: { label: string; description?: string | null; group?: string }) => {
    if (!paletteQueryValue) return true;
    const needle = paletteQueryValue.toLowerCase();
    const haystack = [item.label, item.description, item.group].filter(Boolean).join(" ").toLowerCase();
    return haystack.includes(needle);
  };
  const filteredRoutes = useMemo(
    () => accessibleRoutes.filter(matchesPaletteQuery),
    [accessibleRoutes, paletteQueryValue],
  );
  const filteredActions = useMemo(
    () => accessibleActions.filter(matchesPaletteQuery),
    [accessibleActions, paletteQueryValue],
  );
  const remoteResults = useMemo<CommandPaletteResultEntry[]>(
    () =>
      paletteRemoteResults.map((item) => ({
        id: item.id,
        type: "result",
        label: item.label,
        description: item.description ?? undefined,
        to: item.route ?? undefined,
        group: item.group,
        meta: item.meta ?? undefined,
      })),
    [paletteRemoteResults],
  );
  const paletteSections = useMemo(() => {
    const sections: Array<{ title: string; items: CommandPaletteItem[] }> = [];
    if (paletteQueryValue) {
      if (filteredRoutes.length) sections.push({ title: "Routes", items: filteredRoutes });
      if (filteredActions.length) sections.push({ title: "Actions", items: filteredActions });
      if (remoteResults.length) {
        const grouped = new Map<string, CommandPaletteItem[]>();
        remoteResults.forEach((item) => {
          const groupItems = grouped.get(item.group) ?? [];
          groupItems.push(item);
          grouped.set(item.group, groupItems);
        });
        grouped.forEach((items, group) => {
          sections.push({ title: group, items });
        });
      }
      return sections;
    }

    const routeGroups = new Map<string, CommandPaletteItem[]>();
    accessibleRoutes.forEach((route) => {
      const items = routeGroups.get(route.group) ?? [];
      items.push(route);
      routeGroups.set(route.group, items);
    });
    const groupOrder = ["Go to", "Settings", "Admin & legacy"];
    groupOrder.forEach((group) => {
      const items = routeGroups.get(group);
      if (items?.length) sections.push({ title: group, items });
    });
    routeGroups.forEach((items, group) => {
      if (!groupOrder.includes(group)) {
        sections.push({ title: group, items });
      }
    });
    if (accessibleActions.length) {
      sections.push({ title: "Actions", items: accessibleActions });
    }
    return sections;
  }, [accessibleActions, accessibleRoutes, filteredActions, filteredRoutes, paletteQueryValue, remoteResults]);
  const navPillars = useMemo(
    () =>
      NAV_PILLARS.filter((pillar) => {
        if (pillar.id === "agents-studios" && !canViewAgentsNav) {
          return false;
        }
        if (pillar.id === "marketing" && !canViewMarketingNav) {
          return false;
        }
        if (pillar.id === "control" && !isOwner) {
          return false;
        }
        // Private beta: musicians see Home · Discover · Booking · Shows. Business (operator
        // bookkeeping) stays routable for the platform owner; its page already gates content.
        if (pillar.id === "business" && !isOwner) {
          return false;
        }
        return true;
      }),
    [canViewAgentsNav, canViewMarketingNav, isOwner],
  );
  const activePillar = useMemo(
    () => navPillars.find((pillar) => pillar.id === activeMenu) ?? null,
    [activeMenu, navPillars],
  );

  useEffect(() => {
    setActiveMenu(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!activeMenu) {
      setMenuPosition(null);
      setMenuHover(false);
      return;
    }
    const anchor = navItemRefs.current[activeMenu];
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const width = activePillar?.menuWidth ?? 320;
    const left = Math.min(Math.max(rect.left, 12), window.innerWidth - width - 12);
    const top = rect.bottom + 8;
    setMenuPosition({ left, top });
    setMenuHover(false);
  }, [activeMenu, activePillar]);

  useEffect(() => {
    if (!activeMenu) return;
    const handleReposition = () => {
      const anchor = navItemRefs.current[activeMenu];
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const width = activePillar?.menuWidth ?? 320;
      const left = Math.min(Math.max(rect.left, 12), window.innerWidth - width - 12);
      const top = rect.bottom + 8;
      setMenuPosition({ left, top });
    };
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [activeMenu, activePillar]);

  useEffect(() => {
    return () => {
      if (closeMenuTimeoutRef.current) {
        window.clearTimeout(closeMenuTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setAvatarFallback(false);
  }, [user?.avatar_url]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (!paletteOpen) {
      if (paletteSearchTimeoutRef.current) {
        window.clearTimeout(paletteSearchTimeoutRef.current);
        paletteSearchTimeoutRef.current = null;
      }
      setPaletteLoading(false);
      setPaletteRemoteResults([]);
      setPaletteRemoteError(null);
      return;
    }

    if (paletteQueryValue.length < 2 || !isOwner) {
      setPaletteLoading(false);
      setPaletteRemoteResults([]);
      setPaletteRemoteError(null);
      return;
    }

    if (paletteSearchTimeoutRef.current) {
      window.clearTimeout(paletteSearchTimeoutRef.current);
    }

    const requestId = ++paletteSearchRequestRef.current;
    setPaletteLoading(true);
    paletteSearchTimeoutRef.current = window.setTimeout(() => {
      searchCommandPalette(paletteQueryValue, 28)
        .then((payload) => {
          if (paletteSearchRequestRef.current !== requestId) return;
          setPaletteRemoteResults(payload.results ?? []);
          setPaletteRemoteError(null);
        })
        .catch((error) => {
          if (paletteSearchRequestRef.current !== requestId) return;
          const message = error instanceof Error ? error.message : "Unable to search.";
          setPaletteRemoteError(message);
          setPaletteRemoteResults([]);
        })
        .finally(() => {
          if (paletteSearchRequestRef.current === requestId) {
            setPaletteLoading(false);
          }
        });
    }, 240);

    return () => {
      if (paletteSearchTimeoutRef.current) {
        window.clearTimeout(paletteSearchTimeoutRef.current);
        paletteSearchTimeoutRef.current = null;
      }
    };
  }, [paletteOpen, paletteQueryValue, isOwner]);

  const dispatchPaletteEvent = (detail: Record<string, any>) => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent("command-palette", { detail }));
  };

  const handlePaletteSelect = (item: CommandPaletteItem) => {
    if (item.type === "action") {
      if (item.actionId === "toggle_theme") {
        toggleTheme();
      } else if (item.actionId === "sign_out") {
        signout();
      } else if (item.actionId === "crm_create_contact") {
        navigate("/contacts");
        dispatchPaletteEvent({ type: "crm:create-contact" });
      }
    } else if (item.to) {
      navigate(item.to);
      if (item.type === "result") {
        dispatchPaletteEvent({ type: "command:result-opened", meta: item.meta ?? null });
      }
    }
    setPaletteOpen(false);
    setPaletteQuery("");
  };

  useEffect(() => {
    if (!accountMenuOpen) return;
    const handleClick = (event: MouseEvent) => {
      if (!accountMenuRef.current) return;
      if (!accountMenuRef.current.contains(event.target as Node)) {
        setAccountMenuOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [accountMenuOpen]);

  useEffect(() => {
    if (!helpOpen) return;
    const handleClick = (event: MouseEvent) => {
      if (!helpMenuRef.current) return;
      if (!helpMenuRef.current.contains(event.target as Node)) {
        setHelpOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setHelpOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [helpOpen]);

  useEffect(() => {
    setMobileNavOpen(false);
    setActiveMenu(null);
  }, [location.pathname]);

  const closeMobileNav = () => setMobileNavOpen(false);


  const renderQuickLink = (
    link: NavMenuLink,
    linkKey: string,
    onClick?: () => void,
  ) => {
    const buildLink = (key?: string) => (
      <Link
        key={key}
        to={link.to}
        className="flex min-h-[36px] flex-col justify-center rounded-md px-2.5 py-1.5 text-sm text-foreground/80 transition hover:bg-muted/60 hover:text-primary"
        onClick={() => {
          setActiveMenu(null);
          onClick?.();
        }}
      >
        <span className="block">{link.label}</span>
        {link.description && <span className="text-xs text-muted-foreground">{link.description}</span>}
      </Link>
    );

    if (link.ownerOnly) {
      return (
        <OwnerGate variant="hide" key={linkKey}>
          {buildLink()}
        </OwnerGate>
      );
    }

    return buildLink(linkKey);
  };

  const renderSectionEntry = (
    entry: NavMenuLink | NavMenuGroup,
    entryKey: string,
    onClick?: () => void,
  ) => {
    if (entry.type === "group") {
      return (
        <div key={entryKey} className="rounded-md border border-border/70 bg-background/50 p-1.5">
          <p className="px-1 text-[11px] uppercase tracking-wide text-muted-foreground">{entry.title}</p>
          <div className="mt-1 space-y-0.5">
            {entry.links.map((groupLink, groupIndex) =>
              renderQuickLink(
                groupLink,
                `${entryKey}-group-${groupIndex}`,
                onClick,
              ),
            )}
          </div>
        </div>
      );
    }
    return renderQuickLink(entry, entryKey, onClick);
  };

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-border/60 aa-glass-card">
        <div className="mx-auto w-full max-w-7xl px-2 sm:px-4 pt-1 pb-0">
          <div className="flex items-center gap-4 flex-nowrap">
            <div className="flex items-center gap-3 shrink-0">
              <Link to={logoRoute} className="launch-logo">
                <img className="launch-logo-mark" src="/aafavicon.png" alt="ArtistAscend icon" />
                <span className="launch-logo-text hidden sm:inline">ArtistAscend</span>
              </Link>
            </div>
            <nav
              id="top-nav-links"
              aria-label="Main navigation"
              className="hidden md:block flex-1 overflow-x-hidden"
            >
              <div
                ref={navRowRef}
                className="relative flex items-center gap-2 text-sm min-w-max overflow-visible"
                onMouseEnter={() => {
                  if (closeMenuTimeoutRef.current) {
                    window.clearTimeout(closeMenuTimeoutRef.current);
                    closeMenuTimeoutRef.current = null;
                  }
                }}
                onMouseLeave={() => {
                  if (closeMenuTimeoutRef.current) {
                    window.clearTimeout(closeMenuTimeoutRef.current);
                  }
                  closeMenuTimeoutRef.current = window.setTimeout(() => {
                    if (!menuHover) {
                      setActiveMenu(null);
                    }
                  }, 180);
                }}
              >
                {navPillars.map((pillar) => (
                  <div
                    key={pillar.id}
                    ref={(el) => {
                      navItemRefs.current[pillar.id] = el;
                    }}
                    className="relative"
                    onMouseEnter={() => setActiveMenu(pillar.id)}
                  >
                    <NavLink
                      to={pillar.to}
                      className={({ isActive }) =>
                        `flex items-center gap-1 rounded-lg px-3 py-1.5 transition-all duration-200 ${
                          isActive
                            ? "bg-primary/15 text-primary shadow-sm ring-1 ring-primary/35"
                            : "text-muted-foreground hover:bg-muted/70 hover:text-foreground"
                        }`
                      }
                      onFocus={() => setActiveMenu(pillar.id)}
                      onClick={(event) => {
                        if (pillar.items?.length) {
                          setActiveMenu((prev) => (prev === pillar.id ? null : pillar.id));
                        }
                      }}
                    >
                      {pillar.label}
                      {pillar.items?.length ? <ChevronDown className="h-3 w-3" /> : null}
                    </NavLink>
                  </div>
                ))}
              </div>
            </nav>
            {activePillar?.items?.length && activeMenu && menuPosition && (
              <div
                className="fixed z-50 mt-2 rounded-xl border border-border/60 bg-background/80 backdrop-blur-xl px-2.5 pt-2.5 pb-2 shadow-lg animate-in fade-in-0 zoom-in-95 duration-200"
                style={{ left: menuPosition.left, top: menuPosition.top, width: activePillar.menuWidth ?? 320 }}
                onMouseEnter={() => {
                  setMenuHover(true);
                  if (closeMenuTimeoutRef.current) {
                    window.clearTimeout(closeMenuTimeoutRef.current);
                    closeMenuTimeoutRef.current = null;
                  }
                }}
                onMouseLeave={() => {
                  setMenuHover(false);
                  setActiveMenu(null);
                }}
              >
                <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Quick links</p>
                <div
                  className={
                    activePillar.columns && activePillar.columns > 1 ? "grid gap-4 sm:grid-cols-2" : "space-y-4"
                  }
                >
                  {activePillar.items.map((item) => {
                    const sectionKey = `${activePillar.id}-${item.title}`;
                    const buildSectionContent = (withKey: boolean) => (
                      <div
                        key={withKey ? sectionKey : undefined}
                        className="aa-glass-card rounded-lg p-2.5"
                      >
                        <p className="mb-3 text-[11px] uppercase tracking-wide text-muted-foreground">
                          {item.title}
                        </p>
                        <div
                          className={
                            activePillar.columns && activePillar.columns > 1 ? "grid gap-2 sm:grid-cols-2" : "space-y-2"
                          }
                        >
                          {item.entries.map((entry, entryIndex) =>
                            renderSectionEntry(entry, `${sectionKey}-entry-${entryIndex}`, () => setActiveMenu(null)),
                          )}
                        </div>
                      </div>
                    );

                    if (item.ownerOnly) {
                      return (
                        <OwnerGate variant="hide" key={sectionKey}>
                          {buildSectionContent(false)}
                        </OwnerGate>
                      );
                    }

                    return buildSectionContent(true);
                  })}
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setMobileNavOpen(true)}
                aria-label="Open navigation menu"
                className="aa-glass-card inline-flex items-center justify-center rounded-lg min-h-[44px] min-w-[44px] text-foreground/80 transition-all duration-200 hover:border-border hover:text-foreground hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:hidden"
              >
                <Menu className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => setPaletteOpen((open) => !open)}
                aria-label="Open command palette (Cmd+K)"
                className="aa-glass-card hidden md:inline-flex items-center justify-center gap-1 rounded-lg px-3 min-h-[44px] text-xs font-semibold uppercase tracking-wide text-foreground/80 transition-all duration-200 hover:border-border hover:text-foreground hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <Command className="h-4 w-4" aria-hidden="true" />
                <span className="hidden sm:inline">Cmd+K</span>
                <span className="sm:hidden sr-only">Command palette</span>
              </button>
              <button
                type="button"
                onClick={toggleTheme}
                aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
                className="aa-glass-card hidden md:inline-flex items-center justify-center gap-1 rounded-lg px-3 min-h-[44px] min-w-[44px] text-xs font-semibold uppercase tracking-wide text-foreground/80 transition-all duration-200 hover:border-border hover:text-foreground hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {theme === "dark" ? <Sun className="h-4 w-4" aria-hidden="true" /> : <Moon className="h-4 w-4" aria-hidden="true" />}
                <span className="sr-only">Toggle theme</span>
              </button>
            </div>
            <div className="relative hidden md:block" ref={helpMenuRef}>
              <button
                type="button"
                onClick={() => setHelpOpen((open) => !open)}
                aria-label="Help and support"
                aria-haspopup="menu"
                aria-expanded={helpOpen}
                className="aa-glass-card inline-flex items-center justify-center rounded-lg min-h-[44px] min-w-[44px] text-foreground/80 transition-all duration-200 hover:border-border hover:text-foreground hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <HelpCircle className="h-4 w-4" aria-hidden="true" />
              </button>
              {helpOpen && (
                <div
                  className="aa-glass-card absolute right-0 mt-2 w-56 rounded-xl p-2 shadow-lg animate-in fade-in-0 zoom-in-95 duration-200"
                  role="menu"
                >
                  <p className="px-3 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Help</p>
                  {/* Private beta: the public docs/changelog still describe the pre-beta product. */}
                  {isOwner && (
                    <a
                      href="https://artistascend.app/docs"
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => setHelpOpen(false)}
                      className="flex min-h-[36px] items-center rounded-md px-3 py-2 text-sm text-foreground transition hover:bg-muted/60"
                      role="menuitem"
                    >
                      Documentation
                    </a>
                  )}
                  <a
                    href="mailto:support@artistascend.app"
                    onClick={() => setHelpOpen(false)}
                    className="flex min-h-[36px] items-center rounded-md px-3 py-2 text-sm text-foreground transition hover:bg-muted/60"
                    role="menuitem"
                  >
                    Contact support
                  </a>
                  <a
                    href={`mailto:feedback@artistascend.app?subject=Feedback&body=Hi! I wanted to share some feedback about ArtistAscend.%0A%0A`}
                    onClick={() => setHelpOpen(false)}
                    className="flex min-h-[36px] items-center rounded-md px-3 py-2 text-sm text-foreground transition hover:bg-muted/60"
                    role="menuitem"
                  >
                    Send feedback
                  </a>
                  {isOwner && (
                    <>
                      <div className="my-1 border-t border-border/60" />
                      <a
                        href="https://artistascend.app/changelog"
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => setHelpOpen(false)}
                        className="flex min-h-[36px] items-center justify-between rounded-md px-3 py-2 text-sm text-foreground transition hover:bg-muted/60"
                        role="menuitem"
                      >
                        <span>What's new</span>
                        <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold text-primary">v{__APP_VERSION__}</span>
                      </a>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {isMultiArtist && (
                <div className="hidden md:flex flex-col gap-1 text-left">
                  <label htmlFor="artist-selector" className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Artist
                  </label>
                  <select
                    id="artist-selector"
                    className="mt-1 w-full min-w-0 rounded-md border border-input bg-background px-2 min-h-[40px] text-sm text-foreground md:w-[220px]"
                    value={selectedArtistId ?? ""}
                    onChange={(event) => selectArtist(event.target.value)}
                    disabled={!artistOptions.length || isLoading}
                    aria-label="Select active artist"
                  >
                    <option value="" disabled>
                      {isLoading ? "Loading…" : "Select artist"}
                    </option>
                    {artistOptions.map((artist) => (
                      <option key={artist.id} value={artist.id}>
                        {artist.name || artist.id}
                      </option>
                    ))}
                  </select>
                  {error && <p className="mt-1 text-xs text-destructive" role="alert">{error}</p>}
                </div>
              )}
              {(isOnTrial || trialExpired) && (
                <Link
                  to="/settings/billing"
                  className={`hidden sm:flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition hover:opacity-80 ${
                    trialExpired
                      ? "bg-destructive/20 text-destructive border border-destructive/30"
                      : "bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30"
                  }`}
                  title={trialExpired ? "Trial ended — subscribe to continue" : `Trial ends in ${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"}`}
                >
                  {trialExpired ? "Trial ended" : `${trialDaysLeft}d trial`}
                </Link>
              )}
              <div className="relative" ref={accountMenuRef}>
                <button
                  type="button"
                  onClick={() => setAccountMenuOpen((open) => !open)}
                  aria-haspopup="menu"
                  aria-expanded={accountMenuOpen}
                  className="aa-glass-card flex h-11 w-11 items-center justify-center rounded-full transition hover:border-border"
                >
                  <img
                    src={avatarSrc}
                    alt={user?.name ?? user?.email ?? "Account"}
                    className="h-10 w-10 rounded-full object-cover"
                    onError={() => setAvatarFallback(true)}
                  />
                </button>
                {accountMenuOpen && (
                  <div
                    className="aa-glass-card absolute right-0 mt-2 w-48 rounded-xl p-2 shadow-lg animate-in fade-in-0 zoom-in-95 duration-200"
                    role="menu"
                  >
                    {ACCOUNT_MENU_LINKS.filter((item) => !item.teamOnly || canManageTeam).map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setAccountMenuOpen(false)}
                        className="block rounded-md px-3 py-2 text-sm text-foreground transition hover:bg-muted/60"
                        role="menuitem"
                      >
                        {item.label}
                      </Link>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setAccountMenuOpen(false);
                        signout();
                      }}
                      className="mt-1 w-full rounded-md px-3 py-2 text-left text-sm text-foreground transition hover:bg-muted/60"
                      role="menuitem"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-[55] md:hidden"
          onClick={closeMobileNav}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-md" aria-hidden="true" />
          <div
            className="aa-glass-card relative mx-auto mt-3 w-[calc(100%-1rem)] max-w-md rounded-2xl p-4 text-foreground shadow-xl animate-in fade-in-0 zoom-in-95 duration-200"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                closeMobileNav();
              }
            }}
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Navigation</p>
              <button
                type="button"
                onClick={closeMobileNav}
                aria-label="Close navigation menu"
                className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-4 space-y-4 max-h-[70vh] overflow-y-auto pr-1">
              {isMultiArtist && (
                <div className="rounded-xl border border-border/70 bg-background/40 p-3">
                  <label htmlFor="mobile-artist-selector" className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Artist
                  </label>
                  <select
                    id="mobile-artist-selector"
                    className="mt-2 w-full rounded-md border border-input bg-background px-2 min-h-[44px] text-sm text-foreground"
                    value={selectedArtistId ?? ""}
                    onChange={(event) => selectArtist(event.target.value)}
                    disabled={!artistOptions.length || isLoading}
                    aria-label="Select active artist"
                  >
                    <option value="" disabled>
                      {isLoading ? "Loading…" : "Select artist"}
                    </option>
                    {artistOptions.map((artist) => (
                      <option key={artist.id} value={artist.id}>
                        {artist.name || artist.id}
                      </option>
                    ))}
                  </select>
                  {error && <p className="mt-2 text-xs text-destructive" role="alert">{error}</p>}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setPaletteOpen(true);
                    closeMobileNav();
                  }}
                  className="aa-glass-card inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg px-3 text-xs font-semibold uppercase tracking-wide text-foreground/80"
                >
                  <Command className="h-4 w-4" aria-hidden="true" />
                  Command palette
                </button>
                <button
                  type="button"
                  onClick={toggleTheme}
                  className="aa-glass-card inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg px-3 text-xs font-semibold uppercase tracking-wide text-foreground/80"
                >
                  {theme === "dark" ? <Sun className="h-4 w-4" aria-hidden="true" /> : <Moon className="h-4 w-4" aria-hidden="true" />}
                  Toggle theme
                </button>
              </div>
              <div className="space-y-3">
                {navPillars.map((pillar) => (
                  <div key={pillar.id} className="rounded-xl border border-border/70 bg-background/40 p-3">
                    <NavLink
                      to={pillar.to}
                      className="flex min-h-[44px] items-center justify-between rounded-md px-2 text-sm font-semibold text-foreground transition hover:bg-muted/60"
                      onClick={closeMobileNav}
                    >
                      <span>{pillar.label}</span>
                      {pillar.items?.length ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : null}
                    </NavLink>
                    {pillar.items?.length ? (
                      <div className="mt-2 space-y-2">
                        {pillar.items.map((item, itemIndex) => (
                          <div key={`${pillar.id}-mobile-${itemIndex}`} className="rounded-lg border border-border/60 bg-background/50 p-2">
                            <p className="px-1 text-[11px] uppercase tracking-wide text-muted-foreground">{item.title}</p>
                            <div className="mt-1 space-y-1">
                              {item.entries.map((entry, entryIndex) =>
                                renderSectionEntry(entry, `${pillar.id}-mobile-entry-${entryIndex}`, closeMobileNav),
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
              {/* Secondary support links sit below the primary page navigation. */}
              <nav aria-label="Help and support" className="border-t border-border/60 pt-3">
                <p className="px-1 text-[11px] uppercase tracking-wide text-muted-foreground">Help &amp; Support</p>
                <div className="mt-2 space-y-1">
                  {isOwner && (
                    <a
                      href="https://artistascend.app/docs"
                      target="_blank"
                      rel="noreferrer"
                      onClick={closeMobileNav}
                      className="flex min-h-[40px] items-center rounded-md px-2 text-sm text-muted-foreground transition hover:bg-muted/60 hover:text-foreground"
                    >
                      Documentation
                    </a>
                  )}
                  <a
                    href="mailto:support@artistascend.app"
                    onClick={closeMobileNav}
                    className="flex min-h-[40px] items-center rounded-md px-2 text-sm text-muted-foreground transition hover:bg-muted/60 hover:text-foreground"
                  >
                    Contact support
                  </a>
                  <a
                    href="mailto:feedback@artistascend.app?subject=Feedback"
                    onClick={closeMobileNav}
                    className="flex min-h-[40px] items-center rounded-md px-2 text-sm text-muted-foreground transition hover:bg-muted/60 hover:text-foreground"
                  >
                    Send feedback
                  </a>
                </div>
              </nav>
            </div>
          </div>
        </div>
      )}
      {paletteOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-start justify-center px-2 sm:px-4 py-2 sm:py-10"
          onClick={() => {
            setPaletteOpen(false);
            setPaletteQuery("");
          }}
        >
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-md"
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Command palette"
            className="aa-glass-card relative w-full max-w-xl rounded-xl p-4 sm:p-5 text-foreground shadow-xl animate-in fade-in-0 zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setPaletteOpen(false);
                setPaletteQuery("");
              }
            }}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Command palette</h3>
              <button
                type="button"
                onClick={() => {
                  setPaletteOpen(false);
                  setPaletteQuery("");
                }}
                aria-label="Close command palette (press Escape)"
                className="text-xs uppercase tracking-wide text-muted-foreground hover:text-foreground min-h-[44px] min-w-[44px] inline-flex items-center justify-center"
              >
                Esc
              </button>
            </div>
            <div className="mt-3">
              <label htmlFor="palette-search" className="sr-only">
                Search pages and settings
              </label>
              <input
                id="palette-search"
                type="text"
                value={paletteQuery}
                onChange={(event) => setPaletteQuery(event.target.value)}
                placeholder='Try "discover", "shows", or "billing"...'
                autoFocus
                aria-label="Search pages and settings"
                className="w-full rounded-xl border border-input bg-background/70 px-4 min-h-[44px] text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/70"
              />
              <div className="mt-4 max-h-72 space-y-3 overflow-y-auto" role="listbox" aria-label="Search results">
                {paletteSections.map((section) => (
                  <div key={section.title} className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{section.title}</p>
                    <div className="space-y-2">
                      {section.items.map((item) => {
                        const hint =
                          item.type === "action" ? "Run" : item.type === "result" ? "Open" : "Go";
                        return (
                          <button
                            key={item.id}
                            type="button"
                            role="option"
                            onClick={() => handlePaletteSelect(item)}
                            className="flex w-full items-center justify-between gap-3 rounded-2xl border border-border/70 bg-background/40 px-4 min-h-[44px] text-left text-sm font-semibold text-foreground transition hover:border-primary/50"
                          >
                            <span className="flex flex-col gap-1">
                              <span>{item.label}</span>
                              {item.description ? (
                                <span className="text-xs font-normal text-muted-foreground">{item.description}</span>
                              ) : null}
                            </span>
                            <span className="text-xs uppercase tracking-wide text-muted-foreground">{hint}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {paletteLoading && (
                  <p className="rounded-2xl border border-dashed border-border/60 px-4 py-3 text-xs text-muted-foreground" role="status">
                    Searching…
                  </p>
                )}
                {paletteRemoteError && !paletteLoading && (
                  <p className="rounded-2xl border border-dashed border-border/60 px-4 py-3 text-xs text-destructive" role="status">
                    {paletteRemoteError}
                  </p>
                )}
                {!paletteSections.length && !paletteLoading && !paletteRemoteError && (
                  <p className="rounded-2xl border border-dashed border-border/60 px-4 py-3 text-xs text-muted-foreground" role="status">
                    No matching commands found.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
